import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircleIcon, Loader2, PlugZap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Message,
  MessageContext,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { getUnexpiredToken, useActiveMainCanvas } from "@/utils/utils";
import {
  setEditProjectCandidateId,
  setShowAddEditProjectModal,
  setInitialTab,
} from "@/store/projectsSlice";
import { useStartServer } from "@/utils/devServerUtils";
import { useDispatch } from "react-redux";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { Json } from "@/utils/supabase.types";
import { useSession } from "@clerk/clerk-react";

export const EnvVariablesInputBlock = ({
  title,
  variables,
  message,
}: {
  title: string;
  variables: {
    key: string;
    placeholder: string;
  }[];
  message: Message;
}) => {
  const { session } = useSession();
  const mainCanvas = useActiveMainCanvas();
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dispatch = useDispatch();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const [envVariables, setEnvVariables] = useState<
    {
      key: string;
      value: string;
      placeholder: string;
    }[]
  >([]);

  useEffect(() => {
    if (!variables) return;

    setEnvVariables(
      variables.map((variable) => ({
        key: variable.key,
        value: "",
        placeholder: variable.placeholder,
      }))
    );
  }, [variables]);

  // Check if variables were already saved in message context
  useEffect(() => {
    if (message.message_context?.envVariablesSet) {
      const savedVars = message.message_context.envVariablesSet;

      // Check if all variables in this block have been saved already
      const allSaved = envVariables.every((variable) =>
        savedVars.includes(variable.key)
      );

      if (allSaved && envVariables.length > 0) {
        setSaved(true);
      }
    }
  }, [message.message_context, envVariables]);

  const updateMessageContext = async (updates: Partial<MessageContext>) => {
    if (!message.id || !supabaseClient) return;

    const updatedContext = {
      ...(message.message_context || {}),
      ...updates,
    };

    await supabaseClient
      .from("ai_chat_messages")
      .update({
        message_context: updatedContext as Json,
      })
      .eq("id", message.id);

    message.message_context = updatedContext;
  };

  const restartServer = useStartServer();

  const handleSave = async () => {
    if (!mainCanvas?.project_id) {
      toast.error("No active project found");
      return;
    }

    setIsSaving(true);

    try {
      const envVariablesObj: Record<string, string | null> = {};
      envVariables.forEach((variable) => {
        if (variable.key && variable.value) {
          envVariablesObj[variable.key] = variable.value;
        }
      });

      const supabaseJWT = await getUnexpiredToken(session);

      await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
          mainCanvas.project_id
        }/envVariables`,
        envVariablesObj,
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      // Update message context with saved variable keys (not values)
      await updateMessageContext({
        envVariablesSet: [
          ...(message.message_context?.envVariablesSet || []),
          ...envVariables.map((v) => v.key).filter((k) => k.trim() !== ""),
        ],
      });
      // automatically restart server to apply changes for user
      await restartServer({
        forceRestart: true,
      });

      toast.success("Environment variables saved successfully");
      setSaved(true);
    } catch (error) {
      toast.error("Failed to save environment variables");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 bg-[var(--secondary-n-950)] rounded-md p-2">
      {saved ? (
        <div>
          <div className="flex items-center justify-between">
            <PlugZap className="w-5 h-5 text-secondary-n-600" />
            <Button
              style={{
                borderStyle: "Solid",
                borderWidth: "1px",
              }}
              className="text-secondary-n-50 font-medium h-6 px-2 text-2xs gap-1.5 rounded-md bg-secondary-n-1000 hover:border-none border-2 border-secondary-300"
              onClick={() => {
                dispatch(setEditProjectCandidateId(mainCanvas.project_id));
                dispatch(setInitialTab("env-variables"));
                dispatch(setShowAddEditProjectModal(true));
              }}
            >
              Project settings
            </Button>
          </div>
          <div className="mt-2">
            <p className="text-xs font-medium text-secondary-n-400">
              Your environment variables were successfully added! You can view
              or edit this in your project settings.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 pb-2 pt-1">
            <PlugZap className="w-5 h-5 text-secondary-n-600" />
            <h2 className="font-medium text-xs  text-center">
              {title || "Set Up Your Env Variables"}
            </h2>
          </div>

          <div className="space-y-2">
            {envVariables.map((variable, index) => (
              <div key={index} className="rounded-md">
                <Input
                  className="text-xs bg-background-secondary !border-none rounded-md px-1"
                  disabled={true}
                  value={variable.key}
                  placeholder={variable.key}
                />
                <Input
                  className="text-xs rounded-md p-2 border-secondary-n-950 focus:border-primary-0 bg-secondary-0 placeholder:text-secondary-n-600"
                  type="password"
                  value={
                    saved ? "*****************************" : variable.value
                  }
                  onChange={(e) => {
                    setEnvVariables((prev) => {
                      const copy = [...prev];
                      copy[index].value = e.target.value;
                      return copy;
                    });
                  }}
                  placeholder={variable.placeholder}
                  aria-label="env-variable-value"
                  disabled={isSaving || saved}
                />
              </div>
            ))}

            <div className="flex items-start gap-2 border border-secondary-n-900 bg-secondary-n-1000 rounded-md p-2">
              <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
              <p className="text-xs text-secondary-n-50">
                To connect the app with other services, you need to add API
                keys.
              </p>
            </div>
          </div>
        </div>
      )}

      {!saved && (
        <Button
          onClick={handleSave}
          disabled={
            isSaving ||
            envVariables.some((v) => !v.key?.trim() || !v.value?.trim())
          }
          className="w-full inline-flex items-center justify-center rounded-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary-0 hover:bg-primary-100 text-white shadow px-2.5 py-1.5 text-xs h-7"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      )}
    </div>
  );
};
